import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";

import { CoachLayout } from "~/components/layouts/coach-layout";
import { CoachSiteChrome } from "~/components/layouts/coach-site-chrome";
import { Heading } from "~/components/ui/heading";
import { shouldShowBookingCTAs } from "~/lib/coach-cta-utils";
import { trackBookingPageViewed } from "~/lib/monitoring/track-event";
import { coachQueryOptions } from "~/lib/query-options";
import { createMetaTags, createServiceStructuredData } from "~/lib/shared/meta";

export const Route = createFileRoute("/$username/book/")({
  loader: async ({ context, params }) => {
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    // Refuse in the loader (runs server-side on direct hits) rather than only
    // hiding CTAs: pre-generated (unclaimed) profiles have nobody behind the
    // calendar, and a coach without one has nothing to book.
    if (coach && !shouldShowBookingCTAs(coach)) {
      throw redirect({
        to: "/$username",
        params: { username: coach.username },
      });
    }

    return { coach };
  },
  // Always noindex,follow (#759): the booking page duplicates the profile's
  // copy; followed links keep equity flowing back to the profile. No
  // canonical — a canonical on a noindexed page sends contradictory signals.
  head: ({ loaderData, params }) => {
    const coach = loaderData?.coach;
    const coachName = coach?.fullName ?? params.username;

    const description = `Schedule your coaching call with ${coachName}. Professional coaching consultation to help you achieve your goals.`;
    const imageUrl =
      coach?.profile?.mainImage || coach?.profilePictureUrl || undefined;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/book`;

    const metaTags = createMetaTags({
      title: `Book a Call with ${coachName} | Dream Coach Match`,
      description,
      ogDescription: description,
      ogImage: imageUrl,
      ogUrl: currentUrl,
      ogType: "profile",
      noindexFollow: true,
    });

    if (!coach) return metaTags;

    const structuredData = createServiceStructuredData({
      name: `Coaching Consultation with ${coach.fullName}`,
      description,
      provider: coach.fullName,
      url: currentUrl,
      image: imageUrl,
    });

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: BookingPage,
});

function BookingPage() {
  const { username } = Route.useParams();
  const { coach } = Route.useLoaderData();

  useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  useEffect(() => {
    trackBookingPageViewed(username);
  }, [username]);

  useEffect(() => {
    // Load the GHL form embed script
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.type = "text/javascript";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.head.removeChild(script);
    };
  }, []);

  if (coach == null) {
    return (
      <CoachSiteChrome>
        <div className="p-4">Coach not found</div>
      </CoachSiteChrome>
    );
  }

  if (coach.ghlCalendarId == null) {
    return (
      <CoachSiteChrome>
        <div className="p-4">Calendar not configured.</div>
      </CoachSiteChrome>
    );
  }

  return (
    <CoachSiteChrome>
      <CoachLayout username={username} coach={coach}>
        <section className="z-20 p-4">
          <Heading
            className="lg:mt-12"
            headline={`Schedule your call with ${coach.fullName}`}
            subheadline="Pick a date and schedule your call."
          />
          <iframe
            src={`https://api.leadconnectorhq.com/widget/booking/${coach.ghlCalendarId}`}
            className="mx-auto min-h-screen w-full max-w-5xl overflow-hidden"
          ></iframe>
        </section>
      </CoachLayout>
    </CoachSiteChrome>
  );
}
